/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { I18nService } from '@dm/core';
import { MO } from 'app/common/constants';
import { ToolService } from 'app/common/services/tool.service';
import { LunWorkLoadSimpleModeTypeE } from 'app/feature/service/block-service/lun-group/luns.const';

import { type GuideCommandList } from './smart-assistant';
import { GuideCreateOptions } from './smart-assistant.const';

@Injectable({ providedIn: 'root' })
export class SmartAssistantStrService {
    get = this.i18nService.get;
    /**
     * 是否是白牌场景
     */
    isOem = this.toolService.isOem();
    /**
     * 是否是中文模式
     */
    isZh = this.i18nService.language === 'zh';
    /**
     * 是否是英文模式
     */
    isEn = this.i18nService.language === 'en';

    i18n = {
        ignoreBtn: this.get('common.ignored_btn'),
        knowMoreBtn: this.get('settings.eService_introduce_knowMore_btn'),
        startBtn: this.get('common.start_button1'),
        createMoreBtn: this.get('home.host_continueCreate_btn'),
        modifyHostBtn: this.get('home.host_modifyHost_btn'),
        addMoreBtn: this.get('home.host_continueAdd_btn'),
        mapMoreBtn: this.get('home.host_continueMap_btn'),
        endProcessBtn: this.get('settings.alarmNotification_maintenanceWindow_end_btn'),
        continueProcessBtn: this.get('common.continue_button'),
        createBtn: this.get('common.create_button'),
        addBtn: this.get('common.add_button'),
        mapBtn: this.get('common.map_button'),

        firstTip: this.get('home.smartAssistant_selectSecene_info'),
        noSanFirstTip: this.get('home.smartAssistant_selectSecene_D2100_info'),
        dmeIqTip: this.get('home.smartAssistant_DMEIQ_lab'),
        gifTip1: this.get('home.smartAssistant_figure1_info'),
        gifTip2: this.get('home.smartAssistant_figure2_info'),
        gifTip3: this.get('home.smartAssistant_figure3_info'),
        gifTip4: this.get('home.smartAssistant_figure4_info'),
        provisioningTip1: this.get('home.smartAssistant_operationGuide_multipath_info'),
        provisioningTip2: this.get('home.smartAssistant_operationGuide_info'),
        continueTip: this.get('home.smartAssistant_unfinishedProcess_info'),
        unfinishedProcessTip: this.get('home.smartAssistant_startNewTask_info'),
        finishedProcessTip: this.get('home.smartAssistant_endTask_info'),
        stopGuidingTip: this.get('home.smartAssistant_exitGuide_info'),
        createLunBtnTooltip: this.get('home.host_continueCreate_err_tip'),

        smartAssistantTitle: this.get('home.smartAssistant_lab'),
        scenarioAndApplication: this.get('home.smartAssistant_selectSecene_byScene_lab'),
        oltpLabel: this.get('home.smartAssistant_byScene_OLTP_lab'),
        olapLabel: this.get('home.smartAssistant_byScene_OLTA_lab'),
        olapAndOltpLabel: this.get('home.smartAssistant_byScene_OLTPandOLTA_lab'),
        erpLabel: this.get('home.smartAssistant_byScene_ERP_lab'),
        vdiLabel: this.get('home.smartAssistant_byScene_VDI_lab'),
        videoArchiveLabel: this.get('home.smartAssistant_byScene_VA_lab'),
        sanLabel: this.get('home.smartAssistant_byDataType_san_lab'),
        byDataType: this.get('home.smartAssistant_selectSecene_byDataType_lab'),
        dmeIqLabel: this.get('settings.eService_agreeDataBackhaul_lab'),
        provisioningGuidance: this.get('home.smartAssistant_operationGuide_lab'),
        createLunLabel: this.get('storage.lun_task_create_title'),
        createHostLabel: this.get('home.commonTask_term_createHost_button'),
        addInitiatorLabel: this.get('host.host_task_add_initiator_button'),
        lunMapToHostLabel: this.get('home.smartAssistant_operationGuide_mapToHost_lab'),
        lunLabel: this.get('common.LUNs'),
        lunTitle: this.get('common.lun_title'),
        hostLabel: this.get('common.hosts_title'),
        objDeletedInfo: this.get('home.smartAssistant_noObjects_info'),
        ultraPathUrl: this.get('home.smartAssistant_operationGuide_multipath_link'),
        stopGuiding: this.get('home.smartAssistant_exitGuide_title'),
        endTaskTitle: this.get('home.smartAssistant_endTask_title'),
        startNewTaskTitle: this.get('home.smartAssistant_startNewTask_title'),
        fileSystemTitle: this.get('home.smartAssistant_byDataType_nas_lab'),
    };

    icon = {
        oltp: '#app-icon-smart-assistant-oltp',
        olap: '#app-icon-smart-assistant-olap',
        olapAndOltp: '#app-icon-smart-assistant-oltp-and-olap',
        erp: '#app-icon-smart-assistant-erp',
        vdi: '#app-icon-smart-assistant-vdi',
        videoArchive: '#app-icon-smart-assistant-video-archive',
        san: '#app-icon-smart-assistant-san',
        createLun: '#app-icon-smart-assistant-create-lun',
        createHost: '#app-icon-smart-assistant-create-host',
        addInitiator: '#app-icon-smart-assistant-add-initiator',
        lunMapToHost: '#app-icon-smart-assistant-lun-map-to-host',
    };

    /**
     * 按场景/应用的操作
     */
    applicationOperate: GuideCommandList[] = [
        {
            // 线上分析处理
            icon: this.icon.olap,
            name: this.i18n.olapLabel,
            type: LunWorkLoadSimpleModeTypeE.COMMON_OLAP,
        },
        {
            // 线上交易处理
            icon: this.icon.oltp,
            name: this.i18n.oltpLabel,
            type: LunWorkLoadSimpleModeTypeE.COMMON_OLTP,
        },
        {
            // 线上分析处理&线上交易处理
            icon: this.icon.olapAndOltp,
            name: this.i18n.olapAndOltpLabel,
            type: LunWorkLoadSimpleModeTypeE.COMMON_OLAP_OLTP,
        },
        {
            // ERP 企业资源管理
            icon: this.icon.erp,
            name: this.i18n.erpLabel,
            type: LunWorkLoadSimpleModeTypeE.COMMON_ERP,
        },
        {
            // VDI 虚拟桌面
            icon: this.icon.vdi,
            name: this.i18n.vdiLabel,
            type: LunWorkLoadSimpleModeTypeE.COMMON_VDI,
        },
    ];

    /**
     * 按数据类型的操作
     */
    dataType: GuideCommandList[] = [
        {
            // 块存储
            icon: this.icon.san,
            name: this.i18n.sanLabel,
            type: LunWorkLoadSimpleModeTypeE.DEFAULT,
        },
    ];

    /**
     * “发放引导页”步骤配置
     */
    stepItems = [
        {
            label: this.i18n.createLunLabel,
            icon: this.icon.createLun,
            showInitialBtn: false,
            initialBtnLabel: this.i18n.createBtn,
            btnLabel: this.i18n.createMoreBtn,
            btnTooltip: this.i18n.createLunBtnTooltip,
            objType: GuideCreateOptions.CreateLun,
            url: '/simple/block/lun',
            tab: this.i18n.lunLabel,
        },
        {
            label: this.i18n.createHostLabel,
            icon: this.icon.createHost,
            showInitialBtn: false,
            initialBtnLabel: this.i18n.createBtn,
            btnLabel: this.i18n.modifyHostBtn,
            objType: GuideCreateOptions.CreateHost,
            url: '/simple/block/hosts',
            tab: this.i18n.hostLabel,
            action: 'searchDetail',
        },
        {
            label: this.i18n.addInitiatorLabel,
            icon: this.icon.addInitiator,
            showInitialBtn: false,
            initialBtnLabel: this.i18n.addBtn,
            btnLabel: this.i18n.addMoreBtn,
            objType: GuideCreateOptions.AddInitiator,
            url: '/simple/block/hosts',
            tab: this.i18n.hostLabel,
            action: 'searchDetail',
        },
        {
            label: this.i18n.lunMapToHostLabel,
            icon: this.icon.lunMapToHost,
            showInitialBtn: false,
            initialBtnLabel: this.i18n.mapBtn,
            btnLabel: this.i18n.mapMoreBtn,
            objType: GuideCreateOptions.MapLunToHost,
            url: '/simple/block/hosts',
            tab: this.i18n.hostLabel,
            action: 'searchDetail',
        },
    ];

    createdObjDes = new Map([
        [GuideCreateOptions.CreateLun, (count: number): string => this.get('home.host_createdLUN_info', [count])],
        [GuideCreateOptions.CreateHost, (count: number): string => this.get('home.host_createdHost_info', [count])],
        [GuideCreateOptions.AddInitiator, (count: number, type?: number): string => {
            if (type === MO.FC_TYPE) {
                return this.get('home.host_addedFCInitiator_info', [count]);
            }
            if (type === MO.ISCSI_TYPE) {
                return this.get('home.host_addedIscsiInitiator_info', [count]);
            }
            if (type === MO.NVMeoF_RoCE_TYPE) {
                return this.get('home.host_addedNVMeInitiator_info', [count]);
            }

            return this.get('home.host_addedIscsiInitiator_info', [count]);
        }],
        [GuideCreateOptions.MapLunToHost, (count: number): string => this.get('home.host_mappedLUN_info', [count])],
    ]);

    /**
     * 轮播图对应提示
     */
    gifTips = [
        this.i18n.gifTip1,
        this.i18n.gifTip2,
        this.i18n.gifTip3,
        this.i18n.gifTip4,
    ];

    constructor(
        private i18nService: I18nService,
        private toolService: ToolService,
    ) {
    }

    /**
     * 通过枚举获取“应用类型”的名称
     * @param type 应用类型的枚举值
     */
    getOperateTypeLabel(type: LunWorkLoadSimpleModeTypeE): string {
        const typeList = [...this.applicationOperate, ...this.dataType];
        return typeList.find((item) => item.type === type)?.name;
    }
}
